import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import { type CardMapType } from '@/defs/card'
import { type ClipboardCardType } from '@/defs/clipboard'
import { isPartnerable } from '@/utils/cardUtil'

import { CLIPBOARD_PERSIST_VERSION, clipboardMigrate } from './migrations/clipboardMigrate'

type ClipboardStateType = {
  cards: ClipboardCardType[]
  cardsPending: ClipboardCardType[]
}

type ClipboardCardPayloadType = ClipboardCardType & {
  name?: string
}

type ClipboardStateFullType = {
  prevStates: ClipboardStateType[]
} & ClipboardStateType

const initialState: ClipboardStateFullType = {
  cards: [],
  cardsPending: [],
  prevStates: [],
}

const appendPrevState = (s: ClipboardStateFullType) =>
  [...s.prevStates, { cards: s.cards, cardsPending: s.cardsPending }].slice(-9)

const parseClipboardCard = ({ cardId, isCommander }: ClipboardCardPayloadType): ClipboardCardType => ({
  cardId,
  ...(isCommander && { isCommander }),
})

export const clipboardSlice = createSlice({
  initialState,
  name: 'clipboard',
  reducers: {
    add: (s, { payload: cardsNew }: PayloadAction<ClipboardCardPayloadType[]>) => {
      s.prevStates = appendPrevState(s)
      const cardIds = new Set(cardsNew.map(({ cardId }) => cardId))
      s.cards = [...s.cards.filter(({ cardId }) => !cardIds.has(cardId)), ...cardsNew].map(parseClipboardCard)
      s.cardsPending = s.cardsPending.filter(({ cardId }) => !cardIds.has(cardId))
    },
    addPending: (s, { payload: cardsNew }: PayloadAction<ClipboardCardPayloadType[]>) => {
      const cardIds = new Set([...s.cards.map((r) => r.cardId), ...s.cardsPending.map((r) => r.cardId)])
      s.cardsPending = [...s.cardsPending, ...cardsNew.filter((r) => !cardIds.has(r.cardId))].map(parseClipboardCard)
    },
    clear: (s) => {
      s.prevStates = appendPrevState(s)
      s.cards = []
      s.cardsPending = []
    },
    clearPending: (s) => {
      s.prevStates = appendPrevState(s)
      s.cardsPending = []
    },
    demote: (s, { payload: { cardId } }: PayloadAction<ClipboardCardPayloadType>) => {
      s.prevStates = appendPrevState(s)
      s.cards = [...s.cards.map((c) => (c.cardId === cardId ? { ...c, isCommander: undefined } : c))]
    },
    promote: (
      s,
      { payload: { card, cardMap } }: PayloadAction<{ card: ClipboardCardPayloadType; cardMap: CardMapType }>,
    ) => {
      s.prevStates = appendPrevState(s)
      const newCardId = card.cardId
      const currCommanderIds = s.cards
        .filter((c) => c.isCommander && c.cardId !== newCardId)
        .map(({ cardId }) => cardId)
      const currCommanderId = currCommanderIds.length === 1 ? currCommanderIds[0] : undefined
      const commanderIds =
        currCommanderId && isPartnerable({ cardId: currCommanderId, cardMap, partnerCardId: newCardId })
          ? [currCommanderId, newCardId]
          : [newCardId]
      s.cards = s.cards.map((c) => ({ ...c, isCommander: commanderIds.includes(c.cardId) ? true : undefined }))
    },
    remove: (s, { payload: recs }: PayloadAction<ClipboardCardPayloadType[]>) => {
      s.prevStates = appendPrevState(s)
      const cardIds = recs.map((c) => c.cardId)
      s.cards = s.cards.filter((c) => !cardIds.includes(c.cardId))
      s.cardsPending = s.cardsPending.filter((c) => !cardIds.includes(c.cardId))
    },
    // Undo only undoes the cards array, the cardsPending array remains the same
    undo: (s) => {
      const prevState = s.prevStates.pop()
      s.cards = prevState?.cards || []
    },
  },
})

const persistConfig = {
  key: 'clipboard',
  migrate: clipboardMigrate,
  storage,
  version: CLIPBOARD_PERSIST_VERSION,
  whitelist: ['cards'],
}

export const clipboardActions = clipboardSlice.actions
export const clipboardReducer = persistReducer(persistConfig, clipboardSlice.reducer)
